import React, { useState, useEffect } from 'react';
import { useFeedback } from '../context/FeedbackContext';
import { useLocale } from '../context/LocaleContext';
import { apiService } from '../services/api';
import styles from '../styles/feedbackModal.module.css';

interface FeedbackModalProps {
  userEmail: string;
}

const FEEDBACK_TYPE_KEYS = {
  'General': 'feedback.type.general',
  'Error / Bug': 'feedback.type.bug',
  'Idea': 'feedback.type.idea',
  'Language / Translation': 'feedback.type.translation',
} as const;

type FeedbackType = keyof typeof FEEDBACK_TYPE_KEYS;

const FeedbackModal: React.FC<FeedbackModalProps> = ({ userEmail }) => {
  const { isFeedbackModalOpen, closeFeedbackModal, currentLocation, deviceInfo } = useFeedback();
  const { t, locale } = useLocale();
  const [feedbackType, setFeedbackType] = useState<FeedbackType>('General');
  const [feedbackText, setFeedbackText] = useState('');
  const [emailCopyToUser, setEmailCopyToUser] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!isFeedbackModalOpen) {
      setFeedbackType('General');
      setFeedbackText('');
      setEmailCopyToUser(false);
      setSubmitError(null);
      setSubmitSuccess(false);
    }
  }, [isFeedbackModalOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!feedbackText.trim()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      await apiService.submitFeedback({
        userEmail,
        locationInApp: currentLocation,
        feedbackType,
        feedbackText: feedbackText.trim(),
        deviceInfo,
        emailCopyToUser,
        language: locale,
      });

      setSubmitSuccess(true);

    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : t('feedback.submit_failed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const isSubmitDisabled = !feedbackText.trim() || isSubmitting;
  // Translation reports only make sense while the app is showing a translation.
  const feedbackTypes: FeedbackType[] =
    locale === 'en'
      ? ['General', 'Error / Bug', 'Idea']
      : ['General', 'Error / Bug', 'Idea', 'Language / Translation'];

  if (!isFeedbackModalOpen) {
    return null;
  }

  return (
    <div className={styles.modalOverlay} onClick={closeFeedbackModal}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <div className={styles.headerTop}>
            <h2 className={styles.modalTitle}>{t('feedback.title')}</h2>
            <button
              type="button"
              className={styles.closeButton}
              onClick={closeFeedbackModal}
              aria-label={t('feedback.close_aria')}
            >
              ×
            </button>
          </div>
          <p className={styles.modalSubtitle}>{t('feedback.subtitle')}</p>
        </div>

        {submitSuccess ? (
          <div className={styles.successMessage}>
            <div className={styles.successIcon}>✓</div>
            <h3>{t('feedback.success.title')}</h3>
            <p>{t('feedback.success.message')}</p>
            {emailCopyToUser && (
              <p className={styles.emailConfirmation}>
                {t('feedback.success.copy_emailed', { email: userEmail })}
              </p>
            )}
            <button 
              onClick={closeFeedbackModal}
              className={styles.okButton}
            >
              {t('dialog.ok')}
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className={styles.feedbackForm}>
            <div className={styles.formGroup}>
              <label className={styles.label}>{t('feedback.type_label')}</label>
              <div className={styles.radioGroup}>
                {feedbackTypes.map((type) => (
                  <label key={type} className={styles.radioLabel}>
                    <input
                      type="radio"
                      name="feedbackType"
                      value={type}
                      checked={feedbackType === type}
                      onChange={(e) => setFeedbackType(e.target.value as FeedbackType)}
                      className={styles.radioInput}
                    />
                    <span className={styles.radioText}>{t(FEEDBACK_TYPE_KEYS[type])}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label} htmlFor="feedbackText">
                {t('feedback.text_label')}
              </label>
              <textarea
                id="feedbackText"
                value={feedbackText}
                onChange={(e) => setFeedbackText(e.target.value)}
                placeholder={
                  feedbackType === 'Language / Translation'
                    ? t('feedback.text_placeholder_translation')
                    : t('feedback.text_placeholder')
                }
                className={styles.textarea}
                rows={4}
                required
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={emailCopyToUser}
                  onChange={(e) => setEmailCopyToUser(e.target.checked)}
                  className={styles.checkbox}
                />
                <span className={styles.checkboxText}>{t('feedback.email_copy')}</span>
              </label>
            </div>

            {submitError && (
              <div className={styles.errorMessage}>
                {submitError}
              </div>
            )}

            <div className={styles.formActions}>
              <button
                type="button"
                onClick={closeFeedbackModal}
                className={styles.cancelButton}
                disabled={isSubmitting}
              >
                {t('dialog.cancel')}
              </button>
              <button
                type="submit"
                className={`${styles.submitButton} ${isSubmitDisabled ? styles.disabled : ''}`}
                disabled={isSubmitDisabled}
              >
                {isSubmitting ? t('feedback.submitting') : t('feedback.submit')}
              </button>
            </div>

            <div className={styles.readOnlySection}>
              <div className={styles.compactInfo}>
                <div className={styles.infoRow}>
                  <span className={styles.infoLabel}>{t('feedback.info.user')}</span>
                  <span className={styles.infoValue}>{userEmail}</span>
                </div>
                <div className={styles.infoRow}>
                  <span className={styles.infoLabel}>{t('feedback.info.location')}</span>
                  <span className={styles.infoValue}>{currentLocation}</span>
                </div>
                <div className={styles.infoRow}>
                  <span className={styles.infoLabel}>{t('feedback.info.device')}</span>
                  <span className={styles.infoValue}>
                    {t('feedback.info.device_value', {
                      deviceType: deviceInfo.deviceType,
                      browser: deviceInfo.browser,
                      browserVersion: deviceInfo.browserVersion,
                      os: deviceInfo.operatingSystem,
                    })}
                  </span>
                </div>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default FeedbackModal; 